import * as brazeSDK from '@braze/web-sdk';
import type {
  BrazeEventProperties,
  BrazeInitState,
  BrazeUserAttributes,
  IBrazeService,
} from './types';

const { VITE_BRAZE_API_KEY, VITE_BRAZE_BASE_URL, MODE } = import.meta.env;

const braze = brazeSDK as any;

function getAnonymousUserId(): string {
  const STORAGE_KEY = 'braze_anonymous_user_id';
  let anonymousId = localStorage.getItem(STORAGE_KEY);

  if (!anonymousId) {
    anonymousId = crypto.randomUUID();
    localStorage.setItem(STORAGE_KEY, anonymousId);
  }

  return anonymousId;
}

class BrazeService implements IBrazeService {
  private static instance: BrazeService;
  private state: BrazeInitState = 'not_initialized';
  private currentUserId: string | null = null;
  private queuedEvents: Array<{
    name: string;
    properties?: BrazeEventProperties;
  }> = [];
  private readonly MAX_QUEUED_EVENTS = 50;

  private constructor() {}

  public static getInstance(): BrazeService {
    if (!BrazeService.instance) {
      BrazeService.instance = new BrazeService();
    }
    return BrazeService.instance;
  }

  public async initialize(): Promise<void> {
    if (
      window.location.hostname === 'localhost' ||
      this.state === 'initialized'
    ) {
      return;
    }

    if (!VITE_BRAZE_API_KEY) {
      this.state = 'disabled';
      return;
    }

    this.state = 'initializing';

    try {
      braze.initialize(VITE_BRAZE_API_KEY, {
        baseUrl: VITE_BRAZE_BASE_URL || 'sdk.iad-01.braze.com',
        enableLogging: MODE === 'development',
        sessionTimeoutInSeconds: 1800,
        minimumIntervalBetweenTriggerActionsInSeconds: 30,
      });

      braze.openSession();
      this.state = 'initialized';

      if (!this.currentUserId) {
        this.currentUserId = getAnonymousUserId();
        braze.changeUser(this.currentUserId);
      }

      this.flushQueuedEvents();
    } catch (error) {
      console.error('[Braze] Failed to initialize:', error);
      this.state = 'disabled';
    }
  }

  public isInitialized(): boolean {
    return this.state === 'initialized';
  }

  public getState(): BrazeInitState {
    return this.state;
  }

  public identifyUser(externalId: string): void {
    if (!this.isInitialized() || !externalId) return;

    try {
      if (this.isAnonymousUser()) {
        localStorage.removeItem('braze_anonymous_user_id');
      }

      braze.changeUser(externalId);
      this.currentUserId = externalId;
    } catch (error) {
      console.error('[Braze] Failed to identify user:', error);
    }
  }

  public setUserAttributes(attributes: BrazeUserAttributes): void {
    if (!this.isInitialized()) return;

    try {
      const user = braze.getUser();
      const standardAttrs = [
        'email',
        'first_name',
        'last_name',
      ];

      if (attributes.email) user.setEmail(attributes.email);
      if (attributes.first_name) user.setFirstName(attributes.first_name);
      if (attributes.last_name) user.setLastName(attributes.last_name);

      Object.entries(attributes).forEach(([key, value]) => {
        if (!standardAttrs.includes(key) && value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              user.addToCustomAttributeArray(key, item);
            });
          } else {
            user.setCustomUserAttribute(key, value);
          }
        }
      });
    } catch (error) {
      console.error('[Braze] Failed to set user attributes:', error);
    }
  }

  public logEvent(eventName: string, properties?: BrazeEventProperties): void {
    if (!this.isInitialized()) {
      this.queueEvent(eventName, properties);
      return;
    }

    try {
      braze.logCustomEvent(eventName, properties);
    } catch (error) {
      console.error('[Braze] Failed to log event:', error);
    }
  }

  public logPageView(
    pageName: string,
    properties?: BrazeEventProperties,
  ): void {
    this.logEvent('page_viewed', {
      page_name: pageName,
      page_url: window.location.href,
      page_path: window.location.pathname,
      ...properties,
    });
  }

  public disable(): void {
    if (this.state !== 'initialized') return;

    try {
      braze.disableSDK();
      this.state = 'disabled';
      this.currentUserId = null;
      this.queuedEvents = [];
    } catch (error) {
      console.error('[Braze] Failed to disable SDK:', error);
    }
  }

  private queueEvent(
    eventName: string,
    properties?: BrazeEventProperties,
  ): void {
    if (this.queuedEvents.length >= this.MAX_QUEUED_EVENTS) {
      this.queuedEvents.shift();
    }
    this.queuedEvents.push({ name: eventName, properties });
  }

  private flushQueuedEvents(): void {
    this.queuedEvents.forEach(({ name, properties }) => {
      this.logEvent(name, properties);
    });
    this.queuedEvents = [];
  }

  public getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  public isAnonymousUser(): boolean {
    const anonymousId = localStorage.getItem('braze_anonymous_user_id');
    return this.currentUserId === anonymousId;
  }
}

export const brazeService = BrazeService.getInstance();
